import { AlertTriangle, Brain, ShieldCheck, Zap } from 'lucide-react';
import {
  HallucinationRateChart,
  ReliabilityGapChart,
  MemoryDecayChart,
} from './visualizations';

/**
 * ProblemResearchSection — the quantified industry problem set, shown as the
 * homepage's research layer (dynamic ECharts only, no static pictures).
 *
 * Reuses the blog14 chart components verbatim (they read industryProblems.json),
 * pairs each failure mode with the Raisy capability that solves it, and carries
 * a footnotes block so every number is traceable. Visualizations are templates;
 * only the surrounding content changes here.
 */

const solves = [
  {
    icon: <AlertTriangle className="w-6 h-6" />,
    problem: 'A hallucinated fact can sink a term sheet',
    solution:
      'Every prepared outreach and update passes a human gate until a bot earns autonomy through a transparent confidence score.',
  },
  {
    icon: <Brain className="w-6 h-6" />,
    problem: 'AI forgets an investor\u2019s preference by week two',
    solution:
      'One shared, versioned memory — every investor, every preference, every conversation known across every bot, forever.',
  },
  {
    icon: <ShieldCheck className="w-6 h-6" />,
    problem: 'Unguarded outreach bots fail in production',
    solution:
      'Auditability by design: every send, approval, and update lands on an immutable audit trail you can point to.',
  },
  {
    icon: <Zap className="w-6 h-6" />,
    problem: 'Models churn mid-raise; costs swing 100×',
    solution:
      'Model-agnostic kernels route to the cheapest model that passes the eval — your raise keeps running as models change.',
  },
];

const footnotes = [
  'Hallucination rates: Vectara Hallucination Leaderboard (HHEM-2.3, updated 2026-05-11).',
  'Agent task success: Carnegie Mellon + Salesforce 1,000-task studies; Fiddler synthesis (14.41% agents vs 78.24% humans).',
  'Memory decay: LongMemEval (ICLR 2025) — ~30% accuracy drop across sustained interactions.',
  'Production failure: agentmarketcap.ai, "The 94% AI Production Failure Rate vs 80%+ Benchmark Scores" (2026-04-14).',
  'Model churn: artificialanalysis.ai (605 models / 58 creators, Aug 2026); Stanford HAI AI Index Report 2025.',
];

export const ProblemResearchSection = () => {
  return (
    <section className="bg-slate-50 dark:bg-slate-950 py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-400 rounded-full text-sm font-medium border border-cyan-200 dark:border-cyan-800 mb-4">
            <AlertTriangle className="w-4 h-4" />
            The Problem, in Numbers
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            Why Fundraising Needs a Governed Workforce
          </h2>
          <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
            A raise is the highest-stakes use of AI: one hallucinated fact, one forgotten preference, one unlogged
            send can cost a round. These failure modes are measured and published — and they are exactly what Raisy
            was built to solve. Hover the charts; every number is footnoted below.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
          <HallucinationRateChart />
          <ReliabilityGapChart />
          <MemoryDecayChart />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {solves.map((s, idx) => (
            <div
              key={idx}
              className="p-6 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60"
              data-aos="fade-up"
              data-aos-delay={idx * 100}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-100 to-teal-100 dark:from-cyan-900 dark:to-teal-900 flex items-center justify-center text-cyan-600 dark:text-cyan-400 shrink-0">
                  {s.icon}
                </div>
                <div>
                  <p className="font-semibold text-slate-900 dark:text-white">{s.problem}</p>
                  <p className="text-slate-600 dark:text-slate-300 text-sm mt-1">
                    <span className="text-cyan-700 dark:text-cyan-400 font-medium">Raisy solves it: </span>
                    {s.solution}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-6">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Footnotes</h3>
          <ol className="text-xs text-slate-500 dark:text-slate-400 space-y-1 list-decimal pl-5">
            {footnotes.map((f, idx) => (
              <li key={idx}>{f}</li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};
